import React, { useContext } from 'react';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import { AppContext } from './contexts/AppContext.js';
import { isProjectActive } from './shared/helpers.js';
//Imports Components
import LogIn from './screens/LogIn/LogIn';
import PasswordReset from './screens/passwordReset/PasswordReset.js';
import Home from './screens/Home/Home';
import ErrorPage from './screens/ErrorPage/ErrorPage';
import Finance from './screens/Finance/Finance';
import PMECumplienceReport from './ui-components/containers/reports/reportsPME/PMECumplianceReport.js';
import ComplianceReport from './screens/Reports/ComplianceReport';
import AddProject from './ui-components/containers/addProject/AddProject.js';
import AddProjectForm from './ui-components/Forms/ProjectForm/AddProjectForm.js';
import EditProjectForm from './ui-components/Forms/ProjectForm/EditProjectForm.js';
import UserAdministration from './ui-components/containers/userAdministration/UserAdministration.js';
import AddPME from './ui-components/containers/addPME/AddPME.js';
import PurchasingRequest from './ui-components/containers/puchasingRequest/PurchasingRequest.js';
import ContractService from './ui-components/containers/contractService/ContractService.js';
import FeeContract from './ui-components/containers/feeContract/FeeContract.js';
import PaymentRequest from './ui-components/containers/paymentRequest/PaymentRequest.js';
import FollowUp from './ui-components/containers/followUp/FollowUp.js';
import UserProfile from './ui-components/containers/userProfile/UserProfile.js';
import ProjectView from './ui-components/projectView/ProjectView.js';
import PMEForm from './ui-components/Forms/PmeForm/PMEForm.js';
import RequestTracking from './ui-components/containers/followUp/RequestTracking.js';
import PaymentRequestTracking from './ui-components/containers/followUp/PaymentRequestTracking.js';
import { SEP, UTP, FinanceDepartments, Acquisitions, Legal, InternalControl, Treasury } from './ui-components/containers/departments/departmentsComponents.js';
import SalaryExpenses from './ui-components/containers/salaryExpenses/SalaryExpenses.js';
import IncomeFromSubsidies from './ui-components/containers/subsidies/IncomeFromSubsidies.js';
import BudgetExecution from './ui-components/containers/reports/reportsBudget/BuggetExecution.js';
//Imports Style
import './App.css';
import Loading from './shared/ui-loading/Loading.js';

function App() {
  const { currentUser, loading, userData } = useContext(AuthContext);
  const { dataProjects } = useContext(AppContext);

  const PrivateRoute = ({ children }) => {
    if (loading) {
      return <Loading />
    }

    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }

    if (userData?.permissions !== 'admin' && dataProjects.length === 0) {
      return <Loading />;
    }
    // Si es admin, permitir acceso sin verificar estado del proyecto
    if (userData?.permissions === 'admin') {
      return children;
    }

    // Para usuarios no admin, verificar estado del proyecto
    const allowedInactiveRoutes = [
      '/',
      '/finanzas',
      '/finanzas/ejecucionPME',
      '/finanzas/presupuesto'
    ];

    const isAllowedRoute = allowedInactiveRoutes.some(route =>
      location.pathname === route
    );

    // Verificar estado del proyecto solo para no admins
    if (!isProjectActive(userData?.tenantId, dataProjects) && !isAllowedRoute) {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  const ProtectedDepartmentRoute = ({ children }) => {
    if (loading) {
      return <Loading />
    }

    // Si el usuario no está autenticado, redirigir al login
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }

    // Si el usuario es de establecimiento, redirigir al home
    if (userData?.dimension === 'Establecimiento') {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  const ProtectedEstablishmentRoute = ({ children }) => {
    if (loading) {
      return <Loading />
    }

    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }

    if (userData?.dimension === 'Sostenedor') {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  const router = createBrowserRouter([
    {
      path: '/',
      element: <PrivateRoute><Home /></PrivateRoute>,
      errorElement: <ErrorPage />,
      children: [
        { path: 'perfil', element: <UserProfile /> },
        { path: 'finanzas', element: <Finance /> },
        { path: 'finanzas/ejecucionPME', element: <PMECumplienceReport /> },
        { path: 'finanzas/presupuesto', element: <BudgetExecution /> },
        { path: 'reportes', element: <ComplianceReport /> },
        { path: 'proyectos', element: <AddProject /> },
        { path: 'proyectos/agregar', element: <AddProjectForm /> },
        { path: 'proyectos/editar/:id', element: <EditProjectForm /> },
        { path: 'proyectos/:id', element: <ProjectView /> },
        { path: 'proyectos/usuarios', element: <UserAdministration /> },
        { path: 'establecimiento/pme', element: <AddPME /> },
        { path: 'establecimiento/pme/add', element: <PMEForm /> },
        { path: 'establecimiento/pme/:id', element: <PMEForm /> },
        {
          path: 'establecimiento/compras',
          element: <ProtectedEstablishmentRoute><PurchasingRequest /></ProtectedEstablishmentRoute>
        },
        {
          path: 'establecimiento/servicios',
          element: <ProtectedEstablishmentRoute><ContractService /></ProtectedEstablishmentRoute>
        },
        {
          path: 'establecimiento/honorarios',
          element: <ProtectedEstablishmentRoute><FeeContract /></ProtectedEstablishmentRoute>
        },
        {
          path: 'establecimiento/pagos',
          element: <ProtectedEstablishmentRoute><PaymentRequest /></ProtectedEstablishmentRoute>
        },
        { path: 'establecimiento/seguimiento', element: <FollowUp /> },
        { path: 'establecimiento/seguimiento/:id', element: <RequestTracking /> },
        { path: 'establecimiento/seguimiento-pago/:id', element: <PaymentRequestTracking /> },
        {
          path: 'sostenedor/sep',
          element: <ProtectedDepartmentRoute><SEP /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/utp',
          element: <ProtectedDepartmentRoute><UTP /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/finanzas',
          element: <ProtectedDepartmentRoute><FinanceDepartments /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/adquisiciones',
          element: <ProtectedDepartmentRoute><Acquisitions /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/jurídico',
          element: <ProtectedDepartmentRoute><Legal /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/controlInterno',
          element: <ProtectedDepartmentRoute><InternalControl /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/tesorería',
          element: <ProtectedDepartmentRoute><Treasury /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/remuneraciones',
          element: <ProtectedDepartmentRoute><SalaryExpenses /></ProtectedDepartmentRoute>
        },
        {
          path: 'sostenedor/subsidios',
          element: <ProtectedDepartmentRoute><IncomeFromSubsidies /></ProtectedDepartmentRoute>
        },
      ]
    },
    {
      path: '/login',
      element: <LogIn />
    },
    {
      path: '/password-reset',
      element: <PasswordReset />
    },
  ])

  return (
    <div className="App flex">
      <RouterProvider router={router} />
    </div>
  );
}

export default App;
